import { AxiosProgressEvent } from "axios";
import { ExternalLink, Loader, Upload, X } from "lucide-react";
import {
  ChangeEvent,
  ComponentProps,
  FC,
  MouseEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { createImage, createVideo } from "@/data/api";
import { useDialogState } from "@/hooks/use-dialog-state";
import {
  FileUploadAcceptTypes,
  MaxFileSizeBytes,
  uploadImageToCloudflare,
  uploadVideoToCloudflare,
} from "@/lib/uploads";
import { cn } from "@/lib/utils";

type FileUploadInputProps = ComponentProps<"input"> & {
  fileType: "image" | "video";
  removeDisabled?: boolean;
};

type FileUploadInputInnerProps = {
  name: string;
  value: string;
  openFilePicker: (event: MouseEvent<HTMLButtonElement>) => void;
  isUploading: boolean;
  uploadProgress: number;
  fileType: "image" | "video";
  removeDisabled: boolean;
};

type UploadedImageProps = {
  value: string;
  name: string;
  openFilePicker: (event: MouseEvent<HTMLButtonElement>) => void;
  isUploading: boolean;
  fileType: "image" | "video";
  removeDisabled: boolean;
};

// TODO: Make this more generic
// Switch based on mime type + some parameters like width and height.
type AssetPreviewProps = {
  value: string;
  name: string;
  isUploading: boolean;
  fileType: "image" | "video";
};

// TODO: Refactor this component to use a context and ensure that unused props
// are not passed to inner components.

export const FileUploadInput: FC<FileUploadInputProps> = ({
  value,
  fileType,
  removeDisabled = false,
  ...props
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const { openDialog } = useDialogState();
  const { setValue } = useFormContext();
  const [isUploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleOpenFilePicker = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      hiddenInputRef.current?.click();
    },
    [],
  );

  const handleProgressEvent = useCallback((event: AxiosProgressEvent) => {
    if (event.lengthComputable && event.total) {
      const percent = (event.loaded * 100) / event.total;

      setUploadProgress(Math.round(percent));
    }
  }, []);

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        if (
          (fileType === "image" &&
            file.size > MaxFileSizeBytes.CloudflareImage) ||
          (fileType === "video" && file.size > MaxFileSizeBytes.CloudflareVideo)
        ) {
          openDialog({ name: "asset-size" });
          return;
        }

        try {
          setUploading(true);

          if (fileType === "image") {
            const { result } = await uploadImageToCloudflare(
              file,
              handleProgressEvent,
            );

            const image = await createImage({
              cloudflare_image_id: result.id,
              file_name: file.name,
            });

            if (props.name) {
              setValue(props.name, image.url, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          } else if (fileType === "video") {
            const { result } = await uploadVideoToCloudflare(
              file,
              handleProgressEvent,
            );

            const video = await createVideo({
              cloudflare_video_uid: result.uid,
              file_name: file.name,
            });

            // Cloudflare takes a quick second to process the video and thumbnails
            // we should probably poll the status of the video
            setTimeout(() => {
              if (props.name) {
                setValue(props.name, video.hls_url, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            }, 5000);
          }
        } catch (error) {
          // TODO: We should show a dialog or toast if the upload fails
          console.log(error);
        } finally {
          setUploading(false);
          setUploadProgress(0);
        }
      }
    },
    [fileType, handleProgressEvent, openDialog, props.name, setValue],
  );

  return (
    <div className="min-w-55">
      <FileUploadInputInner
        name={props.name as string}
        fileType={fileType}
        openFilePicker={handleOpenFilePicker}
        value={value as string}
        uploadProgress={uploadProgress}
        isUploading={isUploading}
        removeDisabled={removeDisabled}
      />
      <Input
        {...props}
        ref={hiddenInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept={FileUploadAcceptTypes[fileType]}
      />
    </div>
  );
};

const FileUploadInputInner: FC<FileUploadInputInnerProps> = ({
  openFilePicker,
  value,
  name,
  isUploading,
  uploadProgress,
  fileType,
  removeDisabled,
}) => {
  if (value) {
    return (
      <UploadedImage
        value={value}
        name={name}
        openFilePicker={openFilePicker}
        isUploading={isUploading}
        fileType={fileType}
        removeDisabled={removeDisabled}
      />
    );
  }

  if (isUploading) {
    return (
      <Button variant="secondary" disabled className="w-[146px]" type="button">
        <Progress value={uploadProgress} />
        <span className="w-[48px] text-right">{uploadProgress}%</span>
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      onClick={openFilePicker}
      className="w-[146px]"
      type="button"
    >
      <Upload /> Upload file
    </Button>
  );
};

export const UploadedImage: FC<UploadedImageProps> = ({
  value,
  name,
  openFilePicker,
  isUploading,
  fileType,
  removeDisabled,
}) => {
  const { setValue } = useFormContext();

  const handleDelete = useCallback(() => {
    setValue(name, "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue, name]);

  return (
    <div className="p-3 border flex flex-1 rounded-lg gap-4 justify-between items-center bg-gray-50 min-h-19 min-w-0">
      <div
        className={cn(
          "flex flex-1 min-w-0 gap-2 items-center",
          isUploading ? "opacity-50" : "",
        )}
      >
        <AssetPreview
          value={value as string}
          name={name as string}
          isUploading={isUploading}
          fileType={fileType}
        />
        <p className="font-medium text-primary text-sm truncate">
          {isUploading ? "Uploading file" : value}
        </p>
      </div>
      <div className="flex min-w-0 whitespace-nowrap">
        <Button
          variant="ghost"
          type="button"
          onClick={openFilePicker}
          disabled={isUploading}
        >
          <Upload />
        </Button>
        <Button
          variant="ghost"
          onClick={(event) => event.stopPropagation()}
          className="px-3"
          disabled={isUploading}
          type="button"
        >
          <Link
            to={(value as string)?.replace("/manifest/video.m3u8", "/watch")}
            target="_blank"
          >
            <ExternalLink />
          </Link>
        </Button>
        {!removeDisabled && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleDelete}
            disabled={isUploading}
          >
            <X />
          </Button>
        )}
      </div>
    </div>
  );
};

const AssetPreview: FC<AssetPreviewProps> = ({
  value,
  name,
  fileType,
  isUploading,
}) => {
  if (isUploading) {
    return (
      <div className="flex justify-center items-center w-13 h-13 min-w-13 rounded-md bg-gray-200 animate-pulse">
        <Loader className="w-4 h-4 text-secondary-foreground" />
      </div>
    );
  }

  if (name === "header_logo_url") {
    return (
      <img
        src={value.replace("public", "400")}
        alt="Asset Preview"
        className="max-h-13 object-contain"
      />
    );
  }

  if (fileType === "image") {
    return (
      <img
        src={value.replace("public", "400")}
        alt="Asset Preview"
        className="w-13 h-13 min-w-13 rounded-md object-cover"
      />
    );
  }

  if (fileType === "video") {
    return (
      <img
        src={value.replace("/manifest/video.m3u8", "/thumbnails/thumbnail.jpg")}
        alt="Asset Preview"
        className="w-13 h-13 min-w-13 rounded-md object-cover"
      />
    );
  }

  return null;
};
